import { gql } from 'graphql-request';

export const FIELD_CAPTURE_BATCH_FIELDS = gql`
  fragment FieldCaptureBatchFields on FieldCaptureIngestBatchDto {
    fieldCaptureIngestBatchId
    kind
    pullScope
    filename
    rowsParsed
    storesInScope
    storesAdded
    storesChanged
    storesDropped
    storesUntouched
    rowsQuarantined
    diff
    sourcePullDate
    status
    createdAt
  }
`;

export const FIELD_CAPTURE_STORE_FIELDS = gql`
  fragment FieldCaptureStoreFields on FieldCaptureStoreDto {
    fieldCaptureStoreId
    externalKey
    locationId
    retnum
    terminalId
    currentTermType
    terminalPack
    terminalIndex
    terminalCount
    isPrimaryTerminal
    recordStatus
    storeName
    address
    city
    state
    zip
    chainName
    termPacks
    commType
    commWork
    inScope
    scopeReason
    scheduledDate
    projectId
    linkToken
    status
    openedAt
    submittedAt
    submittedByUserId
    submittedByUserName
    formUrl
    formVersion
    sourcePullDate
    lastSeenPullDate
  }
`;

const FieldCaptureGraphQL = {
  queries: {
    fieldCaptureIngestBatches: gql`
      query fieldCaptureIngestBatches($organizationId: String) {
        fieldCaptureIngestBatches(organizationId: $organizationId) {
          ...FieldCaptureBatchFields
        }
      }
      ${FIELD_CAPTURE_BATCH_FIELDS}
    `,
    fieldCaptureLinkTable: gql`
      query fieldCaptureLinkTable($search: String, $organizationId: String) {
        fieldCaptureLinkTable(search: $search, organizationId: $organizationId) {
          ...FieldCaptureStoreFields
        }
      }
      ${FIELD_CAPTURE_STORE_FIELDS}
    `,
    fieldCaptureProjects: gql`
      query fieldCaptureProjects($organizationId: String) {
        fieldCaptureProjects(organizationId: $organizationId) {
          projectId
          name
        }
      }
    `,
    fieldCaptureEnabled: gql`
      query fieldCaptureEnabled {
        fieldCaptureEnabled
      }
    `,
    // The comp report is built server-side and returned whole; the client
    // saves it via the same Blob path as the links export.
    fieldCaptureBrightstarExport: gql`
      query fieldCaptureBrightstarExport(
        $organizationId: String
        $completedOnly: Boolean
        $from: String
        $to: String
      ) {
        fieldCaptureBrightstarExport(
          organizationId: $organizationId
          completedOnly: $completedOnly
          from: $from
          to: $to
        ) {
          filename
          csv
          rowCount
          generatedAt
        }
      }
    `,
    fieldCaptureOpenVisits: gql`
      query fieldCaptureOpenVisits($organizationId: String) {
        fieldCaptureOpenVisits(organizationId: $organizationId) {
          locationId
          storeName
          retnum
          projectId
          terminalsReported
          terminalsTotal
          lastReportedAt
        }
      }
    `,
    fieldCaptureSerialReview: gql`
      query fieldCaptureSerialReview($organizationId: String) {
        fieldCaptureSerialReview(organizationId: $organizationId) {
          fieldCaptureValueId
          fieldCaptureStoreId
          externalKey
          storeName
          headerKey
          typedValue
          ocrValue
          ocrConfidence
          ocrNotes
          ocrStatus
          source
          mismatch
          prefixValid
          serialRule
          verdict
          adoptedMethod
          rejectedTokens
          photoDocumentId
          updatedAt
        }
      }
    `,
    fieldCaptureQuarantine: gql`
      query fieldCaptureQuarantine {
        fieldCaptureQuarantine {
          fieldCaptureSubmissionId
          tallySubmissionId
          fieldCaptureStoreId
          projectId
          submittedByUserId
          isCurrent
          status
          quarantineReason
          receivedAt
        }
      }
    `,
  },
  mutations: {
    // Sent through the multipart helper (file upload), not graphQlClient.request
    uploadFieldCapturePull: gql`
      mutation uploadFieldCapturePull($input: FieldCaptureUploadInput!) {
        uploadFieldCapturePull(input: $input) {
          ...FieldCaptureBatchFields
        }
      }
      ${FIELD_CAPTURE_BATCH_FIELDS}
    `,
    applyFieldCaptureIngestBatch: gql`
      mutation applyFieldCaptureIngestBatch($batchId: String!, $organizationId: String) {
        applyFieldCaptureIngestBatch(batchId: $batchId, organizationId: $organizationId) {
          ...FieldCaptureBatchFields
        }
      }
      ${FIELD_CAPTURE_BATCH_FIELDS}
    `,
    discardFieldCaptureIngestBatch: gql`
      mutation discardFieldCaptureIngestBatch($batchId: String!, $organizationId: String) {
        discardFieldCaptureIngestBatch(batchId: $batchId, organizationId: $organizationId) {
          ...FieldCaptureBatchFields
        }
      }
      ${FIELD_CAPTURE_BATCH_FIELDS}
    `,
    linkFieldCaptureStoreToProject: gql`
      mutation linkFieldCaptureStoreToProject($externalKey: String!, $projectId: String, $organizationId: String) {
        linkFieldCaptureStoreToProject(externalKey: $externalKey, projectId: $projectId, organizationId: $organizationId) {
          ...FieldCaptureStoreFields
        }
      }
      ${FIELD_CAPTURE_STORE_FIELDS}
    `,
    regenerateFieldCaptureLinks: gql`
      mutation regenerateFieldCaptureLinks($organizationId: String) {
        regenerateFieldCaptureLinks(organizationId: $organizationId)
      }
    `,
    runFieldCaptureOcr: gql`
      mutation runFieldCaptureOcr($organizationId: String, $limit: Int) {
        runFieldCaptureOcr(organizationId: $organizationId, limit: $limit) {
          processed
          read
          failed
          refused
          flaggedForReview
          remainingPending
          haltedReason
        }
      }
    `,
    resolveFieldCaptureSerial: gql`
      mutation resolveFieldCaptureSerial(
        $fieldCaptureValueId: String!
        $value: String!
        $source: FieldCaptureValueSource!
        $organizationId: String
      ) {
        resolveFieldCaptureSerial(
          fieldCaptureValueId: $fieldCaptureValueId
          value: $value
          source: $source
          organizationId: $organizationId
        )
      }
    `,
    backfillFieldCaptureValues: gql`
      mutation backfillFieldCaptureValues($organizationId: String, $dryRun: Boolean) {
        backfillFieldCaptureValues(organizationId: $organizationId, dryRun: $dryRun) {
          submissionsScanned
          submissionsParsed
          valuesWritten
          serialsWithPhoto
          photosUnresolved
          failures
          dryRun
          unmatchedLabels {
            label
            count
          }
        }
      }
    `,
    resolveQuarantinedFieldCaptureSubmission: gql`
      mutation resolveQuarantinedFieldCaptureSubmission($submissionId: String!, $externalKey: String!, $organizationId: String) {
        resolveQuarantinedFieldCaptureSubmission(submissionId: $submissionId, externalKey: $externalKey, organizationId: $organizationId) {
          fieldCaptureSubmissionId
          status
          fieldCaptureStoreId
        }
      }
    `,
  },
};

export default FieldCaptureGraphQL;
